/**
 * @template T
 * @template E
 * @typedef {{ data: T, error: null }} Success
 */

/**
 * @template T
 * @template E
 * @typedef {{ data: null, error: E }} Failure
 */

/**
 * @template T
 * @template E
 * @typedef {Success<T, E> | Failure<T, E>} Result
 */

/**
 * @template T
 * @template E
 * @typedef {Promise<Result<T, E>>} PromiseResult
 */

/**
 * @template T
 * @typedef {Promise<T> | (() => T)} Operation
 */

/**
 * @template T
 * @template E
 * @typedef {PromiseResult<T, E> | Result<T, E>} Output
 */

/**
 * Handles both synchronous and asynchronous operations.
 * @template T
 * @template E
 * @param {() => T | Promise<T>} operation - The operation to handle (sync or async).
 * @returns {Output<T, E>}
 */
export const tryCatch = (operation) => {
  if (typeof operation !== 'function') {
    throw new TypeError('The operation must be a function.');
  }

  const onSuccess = (value) => ({ data: value, error: null });
  const onFailure = (error) => ({ data: null, error });

  try {
    const result = operation();
    if (result instanceof Promise) {
      return result
        .then(onSuccess)
        .catch(onFailure);
    }
    return onSuccess(result);
  } catch (error) {
    return onFailure(error);
  }
};